/**
 * Pure nav transform for the Site Scanner menu entry.
 *
 * The previous release gave clients an opt-in hop into the new Site Scanner via
 * a banner on the old overview page. This one sunsets the old scanner, so the
 * entry point moves up to the menu itself: the backend-supplied "Site Scanner"
 * item under Cookies is rewritten into a custom action that mints an exchange
 * token and redirects, instead of routing to the old Vue view.
 *
 * Kept dependency-free so it can be unit-tested with the Node built-in test
 * runner (the admin portal has no wired-up Jest), mirroring ./nav-mode.js. The
 * reactive glue that reads the config key and the login state lives in
 * shared/state/admin-portal-navigation.js.
 */

/** Top-level "Cookies" nav item, parent of the Site Scanner entry. */
export const COOKIES_NAV_ID = 48

/** The "Site Scanner" child of Cookies. Mirrored in the new app's local-route
 * map (syrenis-frontends/apps/site-scanner/src/config/localRoutes.ts), which
 * keeps the same item in-app once the user is over there. */
export const SITE_SCANNER_NAV_ID = 51

/** Custom-action discriminator resolved in admin-portal/src/app.vue. */
export const SITE_SCANNER_NAV_CUSTOM_ACTION = 'site-scanner-portal'

/**
 * Rewrite the Site Scanner nav item into a cross-portal custom action.
 *
 * Mutates the matched item in place, matching the other nav transforms. The
 * caller owns the gating: this runs only once the config key is on and the
 * user is logged in, so it never provokes an unauthenticated token fetch.
 *
 * Matching by id first and falling back to text mirrors the sibling transforms:
 * the ids are stable, but a tenant with relabelled navigation can still be
 * matched on the default text.
 *
 * @param {Array} navigation top-level nav items, already cloned by the caller
 * @returns {boolean} true when the item was found and rewritten
 */
export function redirectSiteScannerNavItem (navigation) {
	if (!Array.isArray(navigation)) return false
	const cookiesParent = navigation.find(
		item => item.adminPortalNavigationId === COOKIES_NAV_ID || item.text === 'Cookies'
	)
	if (!cookiesParent || !Array.isArray(cookiesParent.sublinks)) return false
	const siteScannerItem = cookiesParent.sublinks.find(
		child => child.adminPortalNavigationId === SITE_SCANNER_NAV_ID || child.text === 'Site Scanner'
	)
	// Absent for a tenant without the module, or a user without the permission.
	// Nothing to do: the redirect must never conjure an entry the backend withheld.
	if (!siteScannerItem) return false
	siteScannerItem.customActionType = SITE_SCANNER_NAV_CUSTOM_ACTION
	return true
}
